import type { i18n } from 'i18next';

import { UiLanguage } from '@/services/ws-companies/src/main/resources/rest-api';

import { LanguageKey } from 'src/types.ts';
import { isEnumValue } from './isEnumValue.ts';

/** The language ws-translate serves texts in: a UI language itself, or a regional variant with texts of its own. */
type TranslationsLanguage = LanguageKey | 'de-AT' | 'de-DE';

type UiLanguageSetting = {
  // what i18next holds and the backends, Intl and dayjs receive
  language: LanguageKey;
  translationsLanguage: TranslationsLanguage;
};

const UI_LANGUAGE_STORAGE_KEY = 'ui-language';

const UI_LANGUAGE_SETTINGS: Record<UiLanguage, UiLanguageSetting> = {
  [UiLanguage.DE]: { language: LanguageKey.German, translationsLanguage: LanguageKey.German },
  [UiLanguage.DE_AT]: { language: LanguageKey.German, translationsLanguage: 'de-AT' },
  [UiLanguage.DE_DE]: { language: LanguageKey.German, translationsLanguage: 'de-DE' },
  [UiLanguage.EN]: { language: LanguageKey.English, translationsLanguage: LanguageKey.English },
  [UiLanguage.FR]: { language: LanguageKey.French, translationsLanguage: LanguageKey.French },
  [UiLanguage.IT]: { language: LanguageKey.Italian, translationsLanguage: LanguageKey.Italian },
};

export const UI_LANGUAGE_LABELS: Record<UiLanguage, string> = {
  [UiLanguage.DE]: 'DE-CH',
  [UiLanguage.DE_AT]: 'DE-AT',
  [UiLanguage.DE_DE]: 'DE-DE',
  [UiLanguage.EN]: 'EN',
  [UiLanguage.FR]: 'FR',
  [UiLanguage.IT]: 'IT',
};

/** The UI languages among `languages`, in the order of `UiLanguage`, whatever order they come in; other values are dropped. */
export const toOrderedUiLanguages = (languages: readonly string[]): UiLanguage[] =>
  Object.values(UiLanguage).filter((uiLanguage) => languages.includes(uiLanguage));

const readStoredUiLanguage = (): UiLanguage | null => {
  const storedUiLanguage = localStorage.getItem(UI_LANGUAGE_STORAGE_KEY);
  return isEnumValue(UiLanguage, storedUiLanguage) ? storedUiLanguage : null;
};

// The UI language of this tab. localStorage, shared by every tab, only seeds it for the next page load: a tab keeps the
// texts it loaded until it switches itself.
let tabUiLanguage = readStoredUiLanguage();

const hasRegionalVariants = (language: LanguageKey) =>
  Object.values(UI_LANGUAGE_SETTINGS).filter((setting) => setting.language === language).length > 1;

/** The UI language i18next's `language` shows: this tab's one when `language` is its language, else the plain one. */
export const getCurrentUiLanguage = (language: string): UiLanguage | undefined => {
  if (tabUiLanguage && UI_LANGUAGE_SETTINGS[tabUiLanguage].language === language) {
    return tabUiLanguage;
  }

  return Object.values(UiLanguage).find((uiLanguage) => UI_LANGUAGE_SETTINGS[uiLanguage].translationsLanguage === language);
};

/** The language ws-translate serves the texts of i18next's `language` in. */
export const getTranslationsLanguage = (language: string): string => {
  const uiLanguage = getCurrentUiLanguage(language);
  return uiLanguage ? UI_LANGUAGE_SETTINGS[uiLanguage].translationsLanguage : language;
};

/** The `loadPath` of the translations backend: the bundles of i18next's languages, requested in their translations language. */
export const getBundleLoadPath = (languages: string[], namespaces: string[]): string =>
  `${languages.map(getTranslationsLanguage).join('+')}.${namespaces.join('+')}`;

/**
 * The versions LocalStorageBackend stamps its cached bundles with, read on every cache access. A regional variant
 * leaves i18next on its language, so a bundle is versioned by the texts it holds as well: a tab never reads a bundle
 * cached for another variant, by itself before a switch or by another tab.
 */
export const createBundleCacheVersions = (deploymentVersion: string): Record<string, string> =>
  new Proxy<Record<string, string>>(
    {},
    {
      get: (_versions, language) =>
        typeof language === 'string' ? `${deploymentVersion}-${getTranslationsLanguage(language)}` : undefined,
    },
  );

/**
 * Stores `uiLanguage` and switches i18next to its language; the language change re-renders the UI, also when i18next
 * was on that language already. The bundles of a language with regional variants may hold another variant's texts,
 * so they are reloaded unless `uiLanguage` is this tab's one already.
 */
const switchUiLanguage = async (i18nInstance: i18n, uiLanguage: UiLanguage) => {
  const { language } = UI_LANGUAGE_SETTINGS[uiLanguage];
  const isReloadNeeded = uiLanguage !== tabUiLanguage && hasRegionalVariants(language);

  if (isReloadNeeded && i18nInstance.language === language) {
    // a bundle still loading lands with the texts it was requested in, so the reload has to come after it
    await new Promise<void>((resolve) => {
      i18nInstance.loadResources(() => resolve());
    });
  }

  tabUiLanguage = uiLanguage;
  localStorage.setItem(UI_LANGUAGE_STORAGE_KEY, uiLanguage);

  const loadedNamespaces = Object.keys(i18nInstance.store.data[language] ?? {});
  if (isReloadNeeded && loadedNamespaces.length > 0) {
    await i18nInstance.reloadResources([language], loadedNamespaces);
  }

  await i18nInstance.changeLanguage(language);
};

let pendingSwitch: Promise<void> = Promise.resolve();

/**
 * Switches the UI to `uiLanguage` once the previous switch is done. i18next folds a reload into a load of the same
 * bundle still in flight, so a variant picked before the previous one arrived would keep the previous one's texts.
 */
export const changeUiLanguage = (i18nInstance: i18n, uiLanguage: UiLanguage): Promise<void> => {
  const switchDone = pendingSwitch.then(() => switchUiLanguage(i18nInstance, uiLanguage));
  // a failed switch must not block the ones after it
  pendingSwitch = switchDone.catch(() => undefined);
  return switchDone;
};

/**
 * The UI language to switch to when `uiLanguages` leave out the one i18next's `language` shows: the first of them in the
 * order of `UiLanguage`. None while they still hold it, or hold no UI language at all.
 */
export const getUiLanguageReplacement = (language: string, uiLanguages: readonly string[]): UiLanguage | undefined => {
  const [firstUiLanguage] = toOrderedUiLanguages(uiLanguages);
  const currentUiLanguage = getCurrentUiLanguage(language);
  return currentUiLanguage && uiLanguages.includes(currentUiLanguage) ? undefined : firstUiLanguage;
};

/** Tells which of two UI languages comes first in the order of `UiLanguage`, the one every list of them shows. */
export const compareUiLanguages = (a: UiLanguage, b: UiLanguage): number =>
  Object.values(UiLanguage).indexOf(a) - Object.values(UiLanguage).indexOf(b);
